/**
 * The guide navigation stack: the guides a traveller has walked through in the preview panel (web)
 * or guide sheet (mobile), so a guide opened from inside another one can go back to it.
 *
 * Pure and platform-neutral (no DOM, no React): each platform's `NavigationPort` keeps a stack in its
 * own history — browser history on web, the guide's React Navigation stack on mobile — and uses these
 * helpers so the rules are the same on both:
 *
 * - **reset** — a guide opened from outside any guide (chat, shortlist, itinerary, stories) starts a
 *   new stack. Back and the breadcrumb only ever describe one exploration. It may start with a
 *   `parent` beneath the guide: a room opens over its hotel's Rooms tab, on the search it came from.
 * - **push** — a link followed inside a guide adds an entry, dropping any forward entries, as browser
 *   history does.
 * - **replace** — a redirect (a place that is really a hotel) swaps the current entry, so Back never
 *   lands on a guide that immediately sends you forward again.
 */

import type { PreviewLinkTarget } from '@elara/domain/schemas/preview-schemas';

import { PreviewTypeSchema } from '@elara/domain/schemas/preview-schemas';
import { z } from 'zod';

/**
 * Per-entry view params a guide restores when it is returned to (e.g. a hotel guide's open tab).
 * Opaque here: the stack carries them and the guide reads them.
 */
export type GuideViewParams = Readonly<Record<string, string>>;

/** What to open: a preview target plus the view to open it on. */
export interface GuideTarget extends PreviewLinkTarget {
  view?: GuideViewParams;
  /** The guide's title when it is known before the guide renders (a parent), for its breadcrumb. */
  title?: string;
}

export interface GuideOpenOptions {
  /**
   * A reset only: the guide to start the stack with, beneath the one being opened, so Back and the
   * breadcrumb lead to it — a room's hotel, on the search the room came from.
   */
  parent?: GuideTarget;
}

/** One guide in the stack. `key` tells two visits to the same guide apart (A → B → A). */
export interface GuideEntry extends GuideTarget {
  readonly key: string;
  /** The guide's own title, captured once it has rendered — the breadcrumb label. */
  readonly title?: string;
}

/**
 * The stack and where the traveller is in it. `key` names the exploration: a reset starts a new key,
 * which is how a platform tells "the same stack, moved along" from "a different stack".
 */
export interface GuideStack {
  readonly key: string;
  readonly entries: readonly GuideEntry[];
  readonly index: number;
}

/** How a guide is being opened — see the module comment. */
export type GuideOpenMode = 'reset' | 'push' | 'replace';

let keyCounter = 0;

/**
 * A key unique across reloads and tabs, since stacks are saved into browser history. Not
 * `crypto.randomUUID`, which React Native's Hermes cannot be relied on to provide.
 */
export function newGuideKey(): string {
  keyCounter += 1;
  return `${Date.now().toString(36)}-${keyCounter.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** The entry being shown, if any. */
export function currentGuide(stack: GuideStack | undefined): GuideEntry | undefined {
  return stack?.entries[stack.index];
}

/** Whether two targets are the same guide on the same view. */
export function isSameGuide(a: GuideTarget, b: GuideTarget): boolean {
  if (a.type !== b.type || a.id !== b.id) return false;
  const aView = a.view ?? {};
  const bView = b.view ?? {};
  const keys = new Set([...Object.keys(aView), ...Object.keys(bView)]);
  for (const key of keys) {
    if ((aView[key] ?? '') !== (bView[key] ?? '')) return false;
  }
  return true;
}

function toEntry({ type, id, view, title }: GuideTarget, newKey: () => string): GuideEntry {
  return {
    type,
    id,
    ...(view && Object.keys(view).length > 0 ? { view } : {}),
    ...(title ? { title } : {}),
    key: newKey(),
  };
}

/**
 * The stack after opening `target` in `mode`. A push or replace with no stack open starts one, and a
 * push or replace of the guide already showing changes nothing — a hotel's map pin pointing at the
 * hotel itself, or a redirect effect running twice, must not add an entry.
 *
 * @example
 * const a = openInStack(undefined, { type: 'place', id: 'p1' }, 'reset');
 * const b = openInStack(a, { type: 'place', id: 'p2' }, 'push'); // entries p1, p2; index 1
 * const c = openInStack(b, { type: 'hotel', id: 'h1' }, 'replace'); // entries p1, h1; index 1
 * const d = openInStack(c, { type: 'room', id: 'r1' }, 'reset', newGuideKey, { parent: { type: 'hotel', id: 'h1' } });
 * // entries h1, r1; index 1
 */
export function openInStack(
  stack: GuideStack | undefined,
  target: GuideTarget,
  mode: GuideOpenMode,
  newKey: () => string = newGuideKey,
  options: GuideOpenOptions = {},
): GuideStack {
  const current = currentGuide(stack);
  if (!stack || !current || mode === 'reset') {
    const { parent } = options;
    return parent
      ? { key: newKey(), entries: [toEntry(parent, newKey), toEntry(target, newKey)], index: 1 }
      : { key: newKey(), entries: [toEntry(target, newKey)], index: 0 };
  }
  if (isSameGuide(current, target)) return stack;

  const entry = toEntry(target, newKey);
  if (mode === 'replace') {
    const entries = [...stack.entries];
    entries[stack.index] = entry;
    return { ...stack, entries };
  }
  return { ...stack, entries: [...stack.entries.slice(0, stack.index + 1), entry], index: stack.index + 1 };
}

/** The stack moved to `index`, or unchanged when `index` is out of range. */
export function moveInStack(stack: GuideStack, index: number): GuideStack {
  if (index < 0 || index >= stack.entries.length || index === stack.index) return stack;
  return { ...stack, index };
}

/**
 * The stack with the current entry's title set. Returns the same object when nothing changes, so a
 * guide can report its title on every render without churning the store.
 */
export function withCurrentTitle(stack: GuideStack, title: string): GuideStack {
  const current = currentGuide(stack);
  if (!current || !title || current.title === title) return stack;
  const entries = [...stack.entries];
  entries[stack.index] = { ...current, title };
  return { ...stack, entries };
}

const GuideStackSchema = z.object({
  key: z.string().min(1),
  entries: z
    .array(
      z.object({
        type: PreviewTypeSchema,
        id: z.string().min(1),
        key: z.string().min(1),
        title: z.string().optional(),
        view: z.record(z.string(), z.string()).optional(),
      }),
    )
    .min(1),
  index: z.number().int().min(0),
});

/**
 * Reads a stack saved outside the app's control — browser `history.state`, which a reload, another
 * build of the app or an extension may have left in any shape. Anything unexpected is `undefined`,
 * never a throw: a lost breadcrumb must not break opening a guide.
 */
export function parseGuideStack(value: unknown): GuideStack | undefined {
  const parsed = GuideStackSchema.safeParse(value);
  if (!parsed.success || parsed.data.index >= parsed.data.entries.length) return;
  return parsed.data;
}
